import { useState } from "react";
import { Lock, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";

/**
 * PickupPinEntry — driver-side companion to PickupPinReveal. 4-digit
 * input grid; submit calls /pickup-pin/verify. On 400 (wrong PIN) we
 * shake the cards and clear input. On 200 we show the green ShieldCheck
 * — the broadcast Socket.IO event will also update the rest of the UI.
 */
export default function PickupPinEntry({ ride, onVerified }) {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);
  const verified = Boolean(ride?.pickup_pin_verified);

  if (verified) {
    return (
      <div data-testid="pin-entry-verified"
        className="flex items-center gap-2 p-3 border border-emerald-400/30 bg-emerald-500/10 rounded-md">
        <ShieldCheck size={16} className="text-emerald-300" />
        <p className="text-[11px] uppercase tracking-wider text-emerald-300">PIN verified · pick-up confirmed</p>
      </div>
    );
  }

  // Hide before driver has accepted the ride
  if (!ride || !ride.driver_email || ride.status === "completed" || ride.status === "cancelled") {
    return null;
  }

  const setDigit = (i, v) => {
    if (v && !/^\d$/.test(v)) return;
    setDigits((cur) => cur.map((d, idx) => (idx === i ? v : d)));
    // Auto-advance focus
    if (v) {
      const next = document.querySelector(`[data-testid="pin-input-${i + 1}"]`);
      if (next) next.focus();
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const pin = digits.join("");
    if (pin.length !== 4) {
      toast.error("Enter the 4-digit PIN");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/chauffeur/rides/${ride.id}/pickup-pin/verify`, { pin });
      toast.success("PIN verified — start the trip");
      onVerified?.(res.data?.ride);
    } catch (err) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setDigits(["", "", "", ""]);
      const first = document.querySelector('[data-testid="pin-input-0"]');
      if (first) first.focus();
      toast.error(errorMessage(err, "Wrong PIN"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="pin-entry-form" onSubmit={submit}
      className="p-3 border border-gold/30 bg-gold/[0.05] rounded-md space-y-2">
      <div className="flex items-center gap-2">
        <Lock size={14} className="text-gold" />
        <p className="text-[11px] uppercase tracking-wider text-gold">Enter rider's 4-digit PIN</p>
      </div>
      <div className={`flex items-center gap-2 ${shake ? "animate-pulse" : ""}`}>
        {digits.map((d, i) => (
          <input
            key={i}
            data-testid={`pin-input-${i}`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={d}
            onChange={(e) => setDigit(i, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Backspace" && !d && i > 0) {
                const prev = document.querySelector(`[data-testid="pin-input-${i - 1}"]`);
                if (prev) prev.focus();
              }
            }}
            className="w-12 h-14 text-center bg-black/50 border border-gold/40 rounded text-2xl font-display text-gold focus:border-gold focus:outline-none"
          />
        ))}
      </div>
      <button data-testid="pin-entry-submit" type="submit" disabled={busy || digits.join("").length !== 4}
        className="w-full inline-flex items-center justify-center gap-2 py-2 bg-gold text-luxury-black text-xs font-body uppercase tracking-wider rounded hover:bg-gold/90 disabled:opacity-40">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
        Verify & start trip
      </button>
    </form>
  );
}
